import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';

const cardStyle = { background: colors.cardBg, boxShadow: colors.shadowCard, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem' };
const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, boxSizing: 'border-box', fontFamily };
const labelStyle = { display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.85rem' };
const btnPrimary = { background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, padding: '0.6rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontFamily };
const btnCancel = { background: 'rgba(255,255,255,0.1)', border: 'none', color: colors.textPrimary, borderRadius: 4, padding: '0.6rem 1.25rem', cursor: 'pointer', fontFamily };

const BookManagement = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActiveUsers, setShowActiveUsers] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);

  const [formData, setFormData] = useState({
    username: '', password: '', name: '',
    weekly_limit: 100000, min_bet: 0.01, max_bet: 10000,
    peak_active_users: 0, billing_enabled: true, is_for_fun: false,
    is_active: true
  });

  useEffect(() => { loadBooks(); }, []);

  const loadBooks = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getBooks();
      setBooks(data.books);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.admin.createBook(formData);
      setShowCreateModal(false);
      setFormData({ username: '', password: '', name: '', weekly_limit: 100000, min_bet: 0.01, max_bet: 10000, peak_active_users: 0, billing_enabled: true, is_for_fun: false });
      loadBooks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create book');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.admin.updateBook(selectedBook.id, {
        name: formData.name,
        password: formData.password,
        is_active: formData.is_active,
      });
      await api.admin.updateBookBilling(selectedBook.id, {
        billing_enabled: formData.billing_enabled,
        is_for_fun: formData.is_for_fun,
      });
      setShowEditModal(false);
      setSelectedBook(null);
      loadBooks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update book');
    }
  };

  const openEditModal = (book) => {
    setSelectedBook(book);
    setFormData({
      name: book.name,
      password: '',
      is_active: book.is_active !== false,
      billing_enabled: book.billing_config?.billing_enabled !== false,
      is_for_fun: book.billing_config?.is_for_fun === true,
    });
    setShowEditModal(true);
  };

  const viewActiveUsers = async (book) => {
    try {
      const data = await api.admin.getBookActiveUsers(book.id);
      setActiveUsers(data.active_users);
      setShowActiveUsers(book);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load active users');
    }
  };

  if (loading) return (
    <div style={{ color: colors.textPrimary, textAlign: 'center', padding: '3rem', fontFamily }}>Loading...</div>
  );

  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalBox = { background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.5rem', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' };
  const modalTitle = { color: colors.textPrimary, margin: '0 0 1.25rem 0', fontSize: '1.1rem', fontFamily };

  const formGroup = { marginBottom: '0.85rem' };

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ color: colors.textPrimary, margin: 0, fontSize: '1.3rem' }}>Book Management</h2>
        <button onClick={() => setShowCreateModal(true)} style={btnPrimary}>+ Create Book</button>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,53,69,0.1)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 6, padding: '0.75rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.25rem' }}>
        {books.map(book => {
          return (
            <div key={book.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: `1px solid ${colors.goldMuted}` }}>
                <h3 style={{ margin: 0, color: colors.textPrimary }}>{book.name}</h3>
                <span style={{ fontSize: '0.75rem', padding: '2px 10px', borderRadius: 20, background: book.is_active ? 'rgba(40,167,69,0.2)' : 'rgba(220,53,69,0.2)', color: book.is_active ? colors.odds : colors.negative }}>
                  {book.is_active ? 'Active' : 'Disabled'}
                </span>
              </div>

              <div style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                <p style={{ color: colors.textMuted, margin: '0.3rem 0' }}><span style={{ color: colors.textPrimary }}>Username:</span> {book.username}</p>
                <p style={{ color: colors.textMuted, margin: '0.3rem 0' }}><span style={{ color: colors.textPrimary }}>Agents:</span> {book.total_agents || 0} &nbsp; <span style={{ color: colors.textPrimary }}>Customers:</span> {book.total_customers || 0}</p>
                <p style={{ color: colors.textMuted, margin: '0.3rem 0' }}><span style={{ color: colors.textPrimary }}>Active Users This Week:</span> {book.active_user_count || 0}</p>
              </div>

              <div style={{ fontSize: '0.82rem', paddingTop: '0.6rem', borderTop: `1px solid ${colors.goldMuted}`, marginBottom: '0.75rem' }}>
                <p style={{ color: colors.gold, fontWeight: 600, margin: '0 0 0.4rem 0', fontSize: '0.8rem' }}>Billing</p>
                <p style={{ color: colors.textMuted, margin: '0.2rem 0' }}>Peak Active Users: <span style={{ color: colors.gold }}>{book.billing_config?.peak_active_users || 0}</span></p>
                <p style={{ color: colors.textMuted, margin: '0.2rem 0' }}>Billing: <span style={{ color: book.billing_config?.billing_enabled !== false ? colors.odds : colors.negative }}>{book.billing_config?.billing_enabled !== false ? 'Enabled' : 'Disabled'}</span></p>
                {book.billing_config?.is_for_fun && (
                  <p style={{ color: colors.textMuted, margin: '0.2rem 0' }}>For Fun: <span style={{ color: colors.freePlay }}>Yes (no site fees)</span></p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.6rem', borderTop: `1px solid ${colors.goldMuted}` }}>
                <button onClick={() => openEditModal(book)} style={{ ...btnPrimary, flex: 1, padding: '0.4rem 0.5rem', fontSize: '0.8rem' }}>Edit</button>
                <button onClick={() => viewActiveUsers(book)} style={{ ...btnPrimary, flex: 1, padding: '0.4rem 0.5rem', fontSize: '0.8rem' }}>Active Users</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={modalOverlay}>
          <div className="scrollbar-hide" style={modalBox}>
            <h3 style={modalTitle}>Create New Book</h3>
            <form onSubmit={handleCreate}>
              <div style={formGroup}><label style={labelStyle}>Username</label><input type="text" style={inputStyle} value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} required /></div>
              <div style={formGroup}><label style={labelStyle}>Password</label><input type="password" style={inputStyle} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required /></div>
              <div style={formGroup}><label style={labelStyle}>Display Name</label><input type="text" style={inputStyle} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></div>
              <p style={{ color: colors.gold, fontWeight: 600, margin: '0.75rem 0 0.5rem 0', fontSize: '0.85rem' }}>Billing</p>
              <div style={formGroup}>
                <label style={labelStyle}>Starting Peak Active Users</label>
                <input type="number" min="0" step="1" style={inputStyle} value={formData.peak_active_users} onChange={e => setFormData({...formData, peak_active_users: parseInt(e.target.value, 10) || 0})} />
                <p style={{ color: colors.textMuted, fontSize: '0.75rem', marginTop: '0.3rem' }}>
                  Determines the starting billing tier. Use 0 for a new book — the tier auto-upgrades as the book grows and never decreases. Only set once, here.
                </p>
              </div>
              <div style={{ ...formGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.billing_enabled} onChange={e => setFormData({...formData, billing_enabled: e.target.checked})} id="billing_enabled_create" />
                <label htmlFor="billing_enabled_create" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>Billing Enabled</label>
              </div>
              <div style={{ ...formGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.is_for_fun} onChange={e => setFormData({...formData, is_for_fun: e.target.checked})} id="is_for_fun_create" />
                <label htmlFor="is_for_fun_create" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>For Fun (book pays no site fees)</label>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                <button type="button" onClick={() => setShowCreateModal(false)} style={{ ...btnCancel, flex: 1 }}>Cancel</button>
                <button type="submit" style={{ ...btnPrimary, flex: 1 }}>Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBook && (
        <div style={modalOverlay}>
          <div className="scrollbar-hide" style={modalBox}>
            <h3 style={modalTitle}>Edit Book: {selectedBook.name}</h3>
            <form onSubmit={handleEdit}>
              <div style={formGroup}><label style={labelStyle}>Display Name</label><input type="text" style={inputStyle} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></div>
              <div style={formGroup}><label style={labelStyle}>New Password (blank = keep)</label><input type="password" style={inputStyle} value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} /></div>
              <div style={{ ...formGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.billing_enabled} onChange={e => setFormData({...formData, billing_enabled: e.target.checked})} id="billing_enabled_edit" />
                <label htmlFor="billing_enabled_edit" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>Billing Enabled</label>
              </div>
              <div style={{ ...formGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.is_for_fun} onChange={e => setFormData({...formData, is_for_fun: e.target.checked})} id="is_for_fun_edit" />
                <label htmlFor="is_for_fun_edit" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>For Fun (book pays no site fees)</label>
              </div>
              <div style={{ ...formGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} id="is_active_edit" />
                <label htmlFor="is_active_edit" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>Account Enabled (uncheck to disable this book)</label>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                <button type="button" onClick={() => setShowEditModal(false)} style={{ ...btnCancel, flex: 1 }}>Cancel</button>
                <button type="submit" style={{ ...btnPrimary, flex: 1 }}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Active Users Modal */}
      {showActiveUsers && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, maxWidth: 640 }}>
            <h3 style={modalTitle}>Active Users — {showActiveUsers.name}</h3>
            <p style={{ color: colors.textMuted, fontSize: '0.82rem', marginBottom: '1rem' }}>Customers who placed bets in the current billing period</p>
            {activeUsers.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                    <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'left', fontWeight: 'normal' }}>Customer</th>
                    <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'left', fontWeight: 'normal' }}>Username</th>
                    <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'center', fontWeight: 'normal' }}>Bets</th>
                    <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'center', fontWeight: 'normal' }}>Parlays</th>
                  </tr>
                </thead>
                <tbody>
                  {activeUsers.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: `1px solid ${colors.sectionBg}` }}>
                      <td style={{ padding: '0.5rem', color: colors.textPrimary }}>{item.customer.name}</td>
                      <td style={{ padding: '0.5rem', color: colors.textMuted }}>{item.customer.username}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', color: colors.gold }}>{item.bet_count}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', color: colors.gold }}>{item.parlay_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: colors.textMuted, textAlign: 'center', padding: '1.5rem' }}>No active users this period</p>
            )}
            <div style={{ marginTop: '1.25rem' }}>
              <button onClick={() => setShowActiveUsers(null)} style={{ ...btnCancel, width: '100%' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookManagement;
